import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Shield, MapPin } from "lucide-react";
import { PrivacyPolicyContent } from "./legal/PrivacyPolicyContent";

interface PrivacyPolicyConsentProps {
  onAccept?: () => void;
}

export const PrivacyPolicyConsent = ({ onAccept }: PrivacyPolicyConsentProps) => {
  const [open, setOpen] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consentGiven = localStorage.getItem("privacy-policy-consent");
    if (!consentGiven) {
      setOpen(true);
    } else {
      setHasConsented(true);
    }
  }, []);

  const handleAccept = () => {
    if (agreedToPolicy) {
      localStorage.setItem("privacy-policy-consent", new Date().toISOString());
      setHasConsented(true);
      setOpen(false);
      onAccept?.();
    }
  };

  const handleDecline = () => {
    // User cannot proceed without accepting
    setAgreedToPolicy(false);
  };

  if (hasConsented) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader className="p-6 pb-0">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-bold">Privacy Policy & User Data Policy</DialogTitle>
              <DialogDescription className="text-base mt-1">
                Please review and accept our comprehensive data protection policy
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="px-6 py-4 max-h-[50vh]">
          <div className="mb-6 p-5 bg-yellow-50 dark:bg-yellow-950/20 border-2 border-yellow-500 rounded-lg">
            <h3 className="text-lg font-bold mb-3 text-yellow-900 dark:text-yellow-100 flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Background Location Permission Required
            </h3>
            <div className="space-y-2 text-sm text-yellow-900 dark:text-yellow-100">
              <p className="font-semibold">
                This app collects location data to enable automated attendance tracking, emergency response, 
                and workforce coordination features <strong>even when the app is closed or not in use</strong>.
              </p>
              <p>
                Background location is only collected during work hours when you are clocked in. You maintain full 
                control and can disable this permission anytime in your device settings.
              </p>
              <p className="text-xs mt-2 font-medium">
                Please review the detailed location usage policy below before accepting.
              </p>
            </div>
          </div>
          
          <PrivacyPolicyContent />
        </ScrollArea>

        <DialogFooter className="p-6 pt-0 flex-col sm:flex-col gap-4">
          <div className="flex items-start gap-3 w-full p-4 bg-primary/5 rounded-lg border border-primary/20">
            <Checkbox
              id="agree-privacy"
              checked={agreedToPolicy}
              onCheckedChange={(checked) => setAgreedToPolicy(checked as boolean)}
              className="mt-1"
            />
            <label
              htmlFor="agree-privacy"
              className="text-sm font-medium leading-relaxed cursor-pointer"
            >
              I have read, understood, and agree to the Privacy Policy and User Data Policy. I specifically consent to 
              <strong> background location data collection</strong> during my work shifts for automated attendance tracking, 
              emergency response, and workforce coordination as described. I understand my data protection rights under 
              GDPR and applicable laws, and I can revoke location permissions at any time.
            </label>
          </div>
          
          <div className="flex gap-3 w-full">
            <Button
              variant="outline"
              onClick={handleDecline}
              className="flex-1"
              disabled={!agreedToPolicy}
            >
              Decline
            </Button>
            <Button
              onClick={handleAccept}
              disabled={!agreedToPolicy}
              className="flex-1"
            >
              Accept & Continue
            </Button>
          </div>
          
          <p className="text-xs text-center text-muted-foreground w-full">
            You must accept this privacy policy to use our services. This policy is compliant with GDPR, CCPA, and app store requirements.
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
